import * as React from "react";
import AppIcon from "../components/AppIcon";
import { useNavigate } from "react-router-dom";
import axios from "axios";

interface Partner {
    id: string;
    name: string;
    description: string;
    logo: string;
    website: string;
}

const GetPartners = async () => {
    return axios.get(`${Server}/api/partners`);
};

const Sponsors = () => {
    const navigate = useNavigate();
    const [partners, setPartners] = React.useState<Partner[]>([]);
    const [isLoading, setIsLoading] = React.useState(true);
    const [error, setError] = React.useState<string | null>(null);

    React.useEffect(() => {
        setIsLoading(true);
        GetPartners()
            .then((res) => {
                if (res.status === 200) {
                    setPartners(res.data);
                } else {
                    setError("Unable to load sponsors");
                }
            })
            .catch((err) => {
                console.error("Error loading sponsors:", err);
                setError("Unable to load sponsors. Please try again later.");
            })
            .finally(() => {
                setIsLoading(false);
            });
    }, []);

    return (
        <div
            className="min-h-screen bg-cover bg-center bg-no-repeat bg-fixed"
            style={{
                backgroundImage: "url(/images/collectivebg-2.jpg)",
            }}
        >
            <div className="min-h-screen backdrop-blur-sm" style={{ backgroundColor: "rgba(241, 245, 248, 0.9)" }}>
                {/* Header Section */}
                <div className="pt-8 pb-6">
                    <div className="max-w-6xl mx-auto px-8">
                        <div className="flex items-center justify-between mb-6">
                            <button
                                onClick={() => navigate("/home")}
                                className="flex items-center gap-2 px-4 py-2 rounded-lg transition-all duration-300 hover:shadow-lg transform hover:-translate-y-1"
                                style={{ backgroundColor: "rgba(121, 153, 186, 0.9)" }}
                            >
                                <span className="text-white text-xl"><AppIcon kind="home" /></span>
                            </button>
                            <div className="text-center w-full">
                                <h1 className="text-2xl font-bold mb-2" style={{ color: "#013A51" }}>
                                    Our Partners
                                </h1>
                                <div className="w-24 h-1 mx-auto rounded-full" style={{ backgroundColor: "#DB1C7D" }}></div>
                            </div>
                            <div className="w-24"></div> {/* Spacer for centering */}
                        </div>
                    </div>
                </div>

                <div className="max-w-6xl mx-auto px-8 pb-8">
                    {/* Hero Section */}
                    <div className="bg-white bg-opacity-60 backdrop-blur-sm rounded-2xl p-8 shadow-lg text-center mb-8" style={{ borderLeft: "4px solid #DB1C7D" }}>
                        <div className="mb-4">
                            <span className="text-6xl"><AppIcon kind="handshake" /></span>
                        </div>
                        <h2 className="text-3xl md:text-4xl font-bold mb-4" style={{ color: "#013A51" }}>
                            Our Amazing Partners
                        </h2>
                        <p className="text-lg" style={{ color: "#708372" }}>
                            Thank you to our wonderful partners who make The Collective Market possible
                        </p>
                    </div>

                    {/* Loading State */}
                    {isLoading && (
                        <div className="flex justify-center items-center py-20">
                            <div className="text-center">
                                <div className="animate-spin rounded-full h-16 w-16 border-b-4 mx-auto mb-4" style={{ borderColor: "#DB1C7D" }}></div>
                                <p className="text-lg" style={{ color: "#013A51" }}>
                                    Loading partners...
                                </p>
                            </div>
                        </div>
                    )}

                    {/* Error State */}
                    {error && !isLoading && (
                        <div className="max-w-md mx-auto">
                            <div className="bg-red-50 border-2 border-red-200 rounded-xl p-6 text-center">
                                <div className="text-red-600 text-5xl mb-4"><AppIcon kind="error" /></div>
                                <h3 className="text-xl font-bold text-red-800 mb-2">Oops!</h3>
                                <p className="text-red-600 mb-4">{error}</p>
                                <button
                                    onClick={() => window.location.reload()}
                                    className="px-6 py-2 rounded-lg font-medium transition-all duration-200"
                                    style={{ backgroundColor: "#DB1C7D", color: "white" }}
                                >
                                    Try Again
                                </button>
                            </div>
                        </div>
                    )}

                    {/* Sponsors Grid */}
                    {!isLoading && !error && partners.length === 0 && (
                        <div className="text-center py-20">
                            <div className="text-6xl mb-4" style={{ color: "#013A51" }}>
                                <AppIcon kind="bullseye" />
                            </div>
                            <h3 className="text-2xl font-bold mb-2" style={{ color: "#013A51" }}>
                                No Partners Yet
                            </h3>
                            <p style={{ color: "#708372" }}>Check back soon for our amazing partners!</p>
                        </div>
                    )}

                    {!isLoading && !error && partners.length > 0 && (
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                            {partners.map((partner, index) => (
                                <div
                                    key={partner.id}
                                    className="group sponsor-card bg-white rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-2 overflow-hidden cursor-pointer"
                                    onClick={() => {
                                        if (partner.website) {
                                            window.open(partner.website, "_blank");
                                        }
                                    }}
                                    style={{
                                        animation: `fadeInUp 0.6s ease-out ${index * 0.1}s both`,
                                    }}
                                >
                                    {/* Logo Section */}
                                    <div className="h-48 bg-gradient-to-br from-gray-50 to-gray-100 flex items-center justify-center p-6 relative overflow-hidden">
                                        <div
                                            className="absolute inset-0 opacity-0 group-hover:opacity-10 transition-opacity duration-300"
                                            style={{ background: "linear-gradient(135deg, #DB1C7D 0%, #013A51 100%)" }}
                                        ></div>
                                        {partner.logo ? (
                                            <img
                                                src={partner.logo}
                                                alt={partner.name}
                                                className="max-w-full max-h-full object-contain relative z-10 transform group-hover:scale-110 transition-transform duration-300"
                                                onError={(e) => {
                                                    e.currentTarget.style.display = "none";
                                                    e.currentTarget.nextElementSibling!.classList.remove("hidden");
                                                }}
                                            />
                                        ) : null}
                                        <div className={`text-6xl ${partner.logo ? "hidden" : ""}`} style={{ color: "#7999BA" }}>
                                            <AppIcon kind="building" />
                                        </div>
                                    </div>

                                    {/* Content Section */}
                                    <div className="p-6">
                                        <h3 className="text-xl font-bold mb-3 group-hover:text-opacity-80 transition-all" style={{ color: "#013A51" }}>
                                            {partner.name}
                                        </h3>

                                        {partner.description && (
                                            <p className="text-sm mb-4 line-clamp-3" style={{ color: "#708372" }}>
                                                {partner.description}
                                            </p>
                                        )}

                                        {partner.website && (
                                            <div className="flex items-center gap-2 text-sm font-medium group-hover:gap-3 transition-all" style={{ color: "#DB1C7D" }}>
                                                <span>Visit Website</span>
                                                <svg className="w-4 h-4 transform group-hover:translate-x-1 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
                                                </svg>
                                            </div>
                                        )}
                                    </div>

                                    {/* Hover Border Effect */}
                                    <div
                                        className="absolute bottom-0 left-0 w-full h-1 transform scale-x-0 group-hover:scale-x-100 transition-transform duration-300 origin-left"
                                        style={{ backgroundColor: "#DB1C7D" }}
                                    ></div>
                                </div>
                            ))}
                        </div>
                    )}

                    {/* Call to Action */}
                    {!isLoading && !error && partners.length > 0 && (
                        <div className="mt-16 text-center bg-gradient-to-r from-indigo-50 to-blue-50 rounded-2xl p-8">
                            <h3 className="text-2xl font-bold mb-3" style={{ color: "#013A51" }}>
                                Interested in Partnering?
                            </h3>
                            <p className="mb-6" style={{ color: "#708372" }}>
                                Join our amazing partners and support The Collective Market
                            </p>
                            <button
                                onClick={() => navigate("/contact")}
                                className="px-8 py-3 rounded-xl font-semibold shadow-lg hover:shadow-xl transform hover:-translate-y-1 transition-all duration-200"
                                style={{ backgroundColor: "#DB1C7D", color: "white" }}
                            >
                                Get in Touch
                            </button>
                        </div>
                    )}
                </div>
            </div>

            <style>{`
                @keyframes fadeInUp {
                    from {
                        opacity: 0;
                        transform: translateY(30px);
                    }
                    to {
                        opacity: 1;
                        transform: translateY(0);
                    }
                }

                .line-clamp-3 {
                    display: -webkit-box;
                    -webkit-line-clamp: 3;
                    -webkit-box-orient: vertical;
                    overflow: hidden;
                }

                .sponsor-card {
                    position: relative;
                }
            `}</style>
        </div>
    );
};

export default Sponsors;
